import React from 'react';
import { GeoJsonLayer } from '@deck.gl/layers';
import type { RGBColor, RGBAColor } from '../deckgl-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectedDateAtom, daySummaryModeAtom, viewingHistoricalAtom } from '../atoms';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { fetchFlatGeobuf } from '../../services/featuresApi';
import type { GeometryVariant } from '../../services/featuresApi';
import { DETAIL_TIMESTAMP_FIELD, THREAT_HISTORY_TABLE } from '../../services/weatherHistory';
import { NIFC_BOUNDS_GEOMETRY_LADDER, useGeometryDetail, type Bbox } from '../geometryDetail';
import { recoveryColor, TING_FILL_ALPHA } from '../../ting/TingOutagesLayer';
import type { PrimaryLayer } from '../../pages/MapPage';
import { viewStateAtom, selectedElevationKeyAtom, selectedElevationScaleAtom } from '../atoms';
import type { PickingInfo } from '@deck.gl/core';
import { visualModeAtom } from '../atoms';
import { POST_EVENT_OF, primaryKeyForSection } from '../../Post/PostLayer';
import { partitionByTimeCategory, FORECAST_LIGHTEN_FACTOR, lightenColor } from '../../wow/WowLayer';
import { percentileBandColor, percentileRanksByGroup } from '../percentileRank';

type CategoryID =
  | 'earthquake1'
  | 'rain1'
  | 'rain2'
  | 'rain3'
  | 'rain4'
  | 'rain5'
  | 'rain6'
  | 'tornado1'
  | 'tornado2'
  | 'tornado3'
  | 'wildfire1'
  | 'wildfire2'
  | 'wildfire3'
  | 'winter1'
  | 'winter2';

const wowColorMap: Record<CategoryID, RGBColor> = {
  earthquake1: [139, 69, 19],
  rain1: [127, 23, 14],
  rain2: [1, 255, 0],
  rain3: [74, 137, 92],
  rain4: [56, 118, 106],
  rain5: [0, 139, 189],
  rain6: [30, 80, 140],
  tornado1: [250, 1, 2],
  tornado2: [255, 254, 0],
  tornado3: [255, 69, 0],
  wildfire1: [181, 9, 9],
  wildfire2: [255, 221, 176],
  wildfire3: [253, 68, 3],
  winter1: [123, 104, 238],
  winter2: [74, 212, 240],
};

type Props = {
  visible?: boolean;
  mapType: PrimaryLayer;
};

const getColorByType = (
  type: 'threat' | 'nifc-threat' | 'canopy-threat',
  category?: string
): RGBAColor => {
  // Object.hasOwn, not `in`: `in` walks the prototype chain, so a feed category
  // named `constructor` would pass the test and then destructure a function
  // into an all-undefined colour. An uncovered category falls through to the
  // per-type default below, as it already did.
  if (category && Object.hasOwn(wowColorMap, category)) {
    const [r, g, b] = wowColorMap[category as CategoryID];
    return [r, g, b, 200];
  }

  switch (type) {
    case 'threat':
    case 'nifc-threat':
      return [181, 9, 9, 100]; // Red for threats
    case 'canopy-threat':
      return [250, 1, 2, 100];
  }
};

export const postColorRange: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

export const getPostFillColor = (d: unknown): RGBAColor => {
  // @ts-expect-error
  const priority_rank = d?.properties?.priority_rank;
  if (typeof priority_rank !== 'number') {
    return [0, 0, 0, 0];
  }
  switch (priority_rank) {
    case 1:
      return postColorRange[0];
    case 2:
      return postColorRange[1];
    case 3:
      return postColorRange[2];
    case 4:
      return postColorRange[3];
    case 5:
      return postColorRange[4];
    default:
      return [0, 0, 0, 0];
  }
};

const getPostHistoryTable = (mapType: PrimaryLayer): string | null => {
  switch (mapType) {
    case 'wow':
      return 'wow_post_history';
    case 'nifc':
      return 'nifc_post_history';
    case 'canopy':
      return 'canopy_post_history';
    // Ting runs no POST model, so there is no per-USNG-cell history table.
    case 'ting':
      return null;
  }
};

// Ting is coloured by its recovery ramp, not by a flat category colour, so it
// never reaches this switch.
const getFeatureType = (
  mapType: Exclude<PrimaryLayer, 'ting'>
): 'threat' | 'nifc-threat' | 'canopy-threat' => {
  switch (mapType) {
    case 'wow':
      return 'threat';
    case 'nifc':
      return 'nifc-threat';
    case 'canopy':
      return 'canopy-threat';
  }
};

// Columns the historical threat layers actually read, so a snapshot carries no
// dead properties: the union of the layer's own accessors and the click popup's
// keys. Adding a consumer of another column means adding it here.
// nifc_bounds_history is absent because attributes are ~0.1% of its geometry-heavy
// payload, and the *_post_history tables because the popup renders all but three
// of their ~40 columns.
const THREAT_HISTORY_FIELDS: Record<string, string[] | undefined> = {
  // threat_id is not displayed but is load-bearing: exposed_structure_polygons
  // rows are keyed by it, so a clicked threat selects the exposure the
  // structures views draw (StructuresLayer.tsx exposureEventForFeature). That
  // table holds only each event's latest revision, so a historical click shows
  // the event's current exposure, not the snapshot's.
  // Dropped: residential_struc_exposed, initial_date -- no accessor and no
  // popup section reads them off a WoW threat feature.
  wow_threats_history: [
    'threat_id',
    'categoryid',
    'threat',
    'category',
    'threatdescription',
    'threatlevel',
    'datasource',
    'time_category',
    'county_intersect',
    'state_intersect',
    'structures_exposed',
    'lastupdatedon',
    'run_time',
  ],
  // canopy_published_date is not displayed but is load-bearing: FeatureInfo
  // detects a canopy feature by its presence and labels the popup from it.
  // kml_id likewise keys the swath's exposure rows (see threat_id above).
  // Dropped: event_date, created_at, last_changed, initial_date,
  // residential_struc_exposed.
  canopy_swaths_history: [
    'kml_id',
    'ef_rank',
    'canopy_published_date',
    'event_name',
    'structures_exposed',
    'county_intersect',
    'state_intersect',
    'fips_intersect',
    'run_time',
  ],
  // Dropped: valid_to and disappeared, the PostGIS-only version bookkeeping
  // (see workflows/RESILIENCY.md) that no view renders.
  ting_outages_history: [
    'event_id',
    'vendor_start',
    'vendor_modified',
    'recovered_percentage',
    'power_state',
    'structures_exposed',
    'residential_struc_exposed',
    'county_intersect',
    'state_intersect',
    'fips_intersect',
    'run_time',
  ],
};

// nifc_bounds_history is the only historical threat table with a generalized
// geometry column, so it is the only one whose fetch names a resolution; every
// other table has one geometry and takes the server's default.
const GENERALIZED_THREAT_HISTORY_TABLE = 'nifc_bounds_history';

type ThreatGeometry = { geometry?: GeometryVariant; bbox?: Bbox };

// Stable identity for the feeds that never name a resolution, so a camera move
// cannot re-run their fetch effect.
const NO_GEOMETRY_VARIANT: ThreatGeometry = {};

// Half-window around the selected snapshot, in minutes.
export const HISTORY_HALF_WINDOW_MINUTES: Record<PrimaryLayer, number> = {
  wow: 15,
  canopy: 15,
  nifc: 60,
  // ting_outages_history is change-based: one run_time stamps exactly one
  // batch of newly observed versions, and the slider snaps to those exact
  // stamps 15 minutes apart. A wider window would overlay two or three polls'
  // versions of the same outage on top of each other.
  ting: 1,
};

export const useWeatherEventsLayer = ({ visible = true, mapType }: Props) => {
  // TimeSlider only commits selectedDate when a drag ends, so each change here
  // is a discrete selection; rapid re-selection aborts the superseded fetch.
  const selectedDate = useAtomValue(selectedDateAtom);
  const daySummaryMode = useAtomValue(daySummaryModeAtom);
  const viewingHistorical = useAtomValue(viewingHistoricalAtom);
  const [fetchedPostData, setPostData] = React.useState<FeatureCollection | null>(null);
  const [fetchedThreatData, setThreatData] = React.useState<FeatureCollection | null>(null);

  const setViewState = useSetAtom(viewStateAtom);
  const elevationScale = useAtomValue(selectedElevationScaleAtom);
  const selectedElevationKey = useAtomValue(selectedElevationKeyAtom);
  const visualMode = useAtomValue(visualModeAtom);
  const extruded = visualMode === '3d';
  const id = `${mapType}-post`;
  const primaryKey = useAtomValue(primaryKeyForSection(id));

  // The snapshot window both historical tables are read over, or null when nothing
  // historical should be drawn. Live view renders the current tables, so fetching
  // the same snapshot from history would draw it twice. `visible` is false on
  // incident routes: time navigation is app-wide, so without it a user who stepped
  // back in time then opened an incident paid for a day-sized window nothing drew.
  const historyWindow = React.useMemo((): { from: Date; to: Date } | null => {
    if (!visible || !selectedDate || !viewingHistorical) {
      return null;
    }

    // daySummaryMode overlays the whole day; otherwise the map renders only the
    // snapshot at the selected time. Snapshots land ~every 15 min, so fetch just
    // that window (one or two snapshots) -- a small indexed range scan -- instead
    // of pulling the whole day (100k+ rows) and discarding all but this window.
    if (daySummaryMode) {
      const from = new Date(selectedDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(selectedDate);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }

    const halfWindowMs = HISTORY_HALF_WINDOW_MINUTES[mapType] * 60 * 1000;
    return {
      from: new Date(selectedDate.getTime() - halfWindowMs),
      to: new Date(selectedDate.getTime() + halfWindowMs),
    };
  }, [visible, selectedDate, viewingHistorical, daySummaryMode, mapType]);

  // A feed with no POST history table, and a closed history window, draw
  // nothing. The last response is declined here rather than cleared from the
  // effects below -- clearing it would leave the outgoing feed's cells on the
  // map for the render pass it takes to land.
  const postTable = getPostHistoryTable(mapType);
  const postData = historyWindow == null || postTable === null ? null : fetchedPostData;
  const threatData = historyWindow == null ? null : fetchedThreatData;

  // Where the simplified column's generalization would show, the NIFC perimeter
  // snapshot is read at full resolution for the viewport only -- the widest
  // national snapshot in nifc_bounds_history measures 16 MB at full resolution
  // against 1,898 kB simplified. Below that the whole snapshot comes back
  // generalized.
  const { base: threatBaseVariant, detail: threatDetail } = useGeometryDetail(
    NIFC_BOUNDS_GEOMETRY_LADDER
  );
  // Until the camera settles the faithful resolution is unknown, so the snapshot
  // is read whole and generalized, as it always has been; a settle a moment later
  // escalates it. Resolved to the variant name before the memo, so the settle
  // that only confirms it does not re-run the fetch below.
  const threatWholeVariant = threatBaseVariant ?? NIFC_BOUNDS_GEOMETRY_LADDER.whole[0].variant;
  const threatGeometry = React.useMemo((): ThreatGeometry => {
    if (THREAT_HISTORY_TABLE[mapType] !== GENERALIZED_THREAT_HISTORY_TABLE) {
      return NO_GEOMETRY_VARIANT;
    }
    if (threatDetail != null) {
      return { geometry: threatDetail.variant, bbox: threatDetail.bbox };
    }
    return { geometry: threatWholeVariant };
  }, [mapType, threatWholeVariant, threatDetail]);

  // Split from the threat fetch below so a camera move, which only the threat
  // request answers to, cannot refetch the POST cells as well.
  React.useEffect(() => {
    if (historyWindow == null || postTable === null) {
      return;
    }

    // Superseded requests are aborted, which cancels the server's in-flight
    // Postgres query too -- scrubbing the slider doesn't pile up backend work.
    const controller = new AbortController();
    const { signal } = controller;

    fetchFlatGeobuf(postTable, {
      dateField: DETAIL_TIMESTAMP_FIELD[mapType],
      ...historyWindow,
      signal,
    })
      .then(fc => {
        if (!signal.aborted) setPostData(fc);
      })
      .catch(err => {
        if (signal.aborted) return;
        console.error('Error fetching POST history:', err);
        setPostData(null);
      });

    return () => {
      controller.abort();
    };
  }, [mapType, postTable, historyWindow]);

  // Polygons already drawn stay until the replacement resolves, so a resolution swap
  // or pan never blanks them, but a pan past the padding shows the newly revealed
  // ground empty until the next fetch lands. No national backdrop here (unlike
  // NIFC/NifcLayer.tsx) because every slider step changes this window, so each step
  // would refetch the whole snapshot.
  React.useEffect(() => {
    if (historyWindow == null) {
      return;
    }

    const threatTable = THREAT_HISTORY_TABLE[mapType];
    const controller = new AbortController();
    const { signal } = controller;

    fetchFlatGeobuf(threatTable, {
      dateField: DETAIL_TIMESTAMP_FIELD[mapType],
      ...historyWindow,
      ...threatGeometry,
      signal,
      fields: THREAT_HISTORY_FIELDS[threatTable],
    })
      .then(fc => {
        if (!signal.aborted) setThreatData(fc);
      })
      .catch(err => {
        if (signal.aborted) return;
        console.error('Error fetching threat history:', err);
        setThreatData(null);
      });

    return () => {
      controller.abort();
    };
  }, [mapType, historyWindow, threatGeometry]);

  const handleElevation = React.useCallback(
    (d: Feature<Geometry, unknown>): number => {
      const value = (d.properties as Record<string, unknown>)?.[selectedElevationKey];
      if (typeof value === 'number') {
        return value;
      }
      return 0;
    },
    [selectedElevationKey]
  );

  const handleClick = React.useCallback(
    (info: PickingInfo) => {
      if (visible && info?.object && info.coordinate) {
        setViewState(view => ({
          ...view,
          maxPitch: 85,
          longitude: info.coordinate![0],
          latitude: info.coordinate![1],
        }));
      }
    },
    [visible, setViewState]
  );

  // The snapshot's cells ranked by the "Color by" attribute within their
  // event, the same way the live grid ranks them (GridCellLayer), so the
  // legend describes both. Ting runs no POST model, so it has no event key.
  const eventOf = mapType === 'ting' ? null : POST_EVENT_OF[mapType];
  const percentileData = React.useMemo(() => {
    if (postData == null) {
      return new Map<unknown, Map<number, number>>();
    }
    const cells: { group: unknown; value: number }[] = [];
    for (const feature of postData.features) {
      const properties = feature.properties as Record<string, unknown> | null;
      const value = properties?.[primaryKey];
      if (typeof value === 'number') {
        cells.push({ group: eventOf == null ? null : eventOf(properties), value });
      }
    }
    return percentileRanksByGroup(cells);
  }, [postData, primaryKey, eventOf]);

  const getPercentileRankFillColor = React.useCallback(
    (d: Feature<Geometry, unknown>): RGBAColor => {
      if (d.type === 'Feature' && visible && primaryKey) {
        const properties = d.properties as Record<string, unknown> | null;
        const value = properties?.[primaryKey];
        if (typeof value !== 'number') {
          return [0, 0, 0, 0];
        }
        const group = eventOf == null ? null : eventOf(properties);
        return percentileBandColor(percentileData.get(group)?.get(value));
      }
      return [0, 0, 0, 0];
    },
    [percentileData, primaryKey, visible, eventOf]
  );

  const handlePostFillColor = React.useMemo(
    () => (primaryKey === 'priority_rank' ? getPostFillColor : undefined),
    [primaryKey]
  );

  const postLayer = postData
    ? [
        new GeoJsonLayer({
          id: `weather-${mapType}-events-layer-post`,
          data: postData,
          pickable: true,
          pointRadiusMinPixels: 3,
          stroked: false,
          pointRadiusMaxPixels: 10,
          pointRadiusScale: 6,
          getFillColor:
            handlePostFillColor ?? (primaryKey ? getPercentileRankFillColor : getPostFillColor),
          getPointRadius: 1000,
          lineWidthMinPixels: 1,
          visible,
          extruded,
          elevationScale,
          getElevation: handleElevation,
          material: true,
          wireframe: false,
          onClick: handleClick,
          updateTriggers: {
            getFillColor: [selectedDate, postData, mapType, primaryKey],
            getElevation: [extruded, handleElevation],
            elevationScale: [extruded, elevationScale],
          },
        }),
      ]
    : [];

  const threatFillColor = React.useCallback(
    (d: unknown): RGBAColor => {
      const feature = d as { properties?: Record<string, unknown> };
      if (mapType === 'ting') {
        const recovered = feature.properties?.recovered_percentage;
        return recoveryColor(typeof recovered === 'number' ? recovered : null, TING_FILL_ALPHA);
      }
      let category = '';
      if (mapType === 'wow') {
        category = (feature.properties?.categoryid as string) ?? '';
      } else if (mapType === 'canopy') {
        category = `EF${feature.properties?.ef_rank ?? 'Unknown'}`;
      } else if (mapType === 'nifc') {
        category = 'NIFC';
      }
      return getColorByType(getFeatureType(mapType), category);
    },
    [mapType]
  );

  // The forecast half of a WoW snapshot is lightened as the live forecast
  // perimeters are, so the two stay telling apart where they overlap.
  const forecastFillColor = React.useCallback(
    (d: unknown): RGBAColor => {
      const [r, g, b, alpha] = threatFillColor(d);
      return [...lightenColor([r, g, b], FORECAST_LIGHTEN_FACTOR), alpha];
    },
    [threatFillColor]
  );

  // The live WoW perimeters draw as separate forecast and observed layers, each
  // with its own eye in the layer list, so the snapshot is split the same way
  // and each half carries the id its leaf claims: the forecast half is what
  // time_category marks 'future', the observed half is every other row, so a
  // row with an unexpected value is still drawn. The other feeds draw one
  // threats layer.
  const threatCollections = React.useMemo((): {
    id: string;
    data: FeatureCollection;
    getFillColor: (d: unknown) => RGBAColor;
  }[] => {
    if (threatData == null) {
      return [];
    }
    if (mapType !== 'wow') {
      return [
        {
          id: `weather-${mapType}-events-layer-threats`,
          data: threatData,
          getFillColor: threatFillColor,
        },
      ];
    }
    const { matching: forecast, rest: observed } = partitionByTimeCategory(threatData, 'future');
    return [
      {
        id: 'weather-wow-events-layer-threats-forecast',
        data: forecast,
        getFillColor: forecastFillColor,
      },
      {
        id: 'weather-wow-events-layer-threats-observed',
        data: observed,
        getFillColor: threatFillColor,
      },
    ];
  }, [threatData, mapType, threatFillColor, forecastFillColor]);

  const threatsLayer = threatCollections.map(
    ({ id: layerId, data, getFillColor }) =>
      new GeoJsonLayer({
        id: layerId,
        data,
        pickable: true,
        pointRadiusMinPixels: 3,
        pointRadiusMaxPixels: 10,
        pointRadiusScale: 6,
        getFillColor,
        stroked: false,
        getLineColor: [255, 255, 255],
        getPointRadius: 1000,
        lineWidthMinPixels: 1,
        visible,
        extruded,
        elevationScale,
        getElevation: handleElevation,
        material: true,
        wireframe: false,
        onClick: handleClick,
        updateTriggers: {
          getFillColor: [selectedDate, data, getFillColor],
          getElevation: [extruded, handleElevation],
          elevationScale: [extruded, elevationScale],
        },
      })
  );

  if (mapType === 'canopy') {
    return [postLayer, threatsLayer];
  }

  return [threatsLayer, postLayer];
};
